import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

type SlotState = 'idle' | 'charge' | 'discharge'

const RADIUS = 52
const CIRCUMFERENCE = 2 * Math.PI * RADIUS
const STATE_OF_CHARGE = 72
const CURRENT_HOUR = 13

const plan: SlotState[] = Array.from({ length: 24 }, (_, hour) => {
    if (hour >= 11 && hour <= 14) return 'charge'
    if (hour >= 17 && hour <= 20) return 'discharge'

    return 'idle'
})

const slotStyles: Record<SlotState, string> = {
    idle: 'bg-mist',
    charge: 'bg-primary',
    discharge: 'bg-chart-2'
}

const facts = [
    { label: 'Charging now', value: '+42 MW' },
    { label: 'Full by', value: '15:10' },
    { label: 'Held for the peak', value: '520 MWh' }
]

const BalanceVisual: FC = (): ReactNode => {
    return (
        <div>
            <div className='flex items-center gap-6 sm:gap-8'>
                <div
                    role='img'
                    aria-label={`Battery state of charge ${STATE_OF_CHARGE}%`}
                    className='relative size-28 shrink-0 sm:size-32'
                >
                    <svg viewBox='0 0 120 120' className='size-full -rotate-90'>
                        <circle cx='60' cy='60' r={RADIUS} fill='none' strokeWidth='10' className='stroke-leaf-soft' />
                        <circle
                            cx='60'
                            cy='60'
                            r={RADIUS}
                            fill='none'
                            strokeWidth='10'
                            strokeLinecap='round'
                            strokeDasharray={CIRCUMFERENCE}
                            strokeDashoffset={CIRCUMFERENCE * (1 - STATE_OF_CHARGE / 100)}
                            className='stroke-primary'
                        />
                    </svg>

                    <div className='absolute inset-0 flex flex-col items-center justify-center'>
                        <span className='font-heading text-3xl font-light tracking-tight text-foreground'>
                            {STATE_OF_CHARGE}%
                        </span>
                        <span className='text-[11px] text-muted-foreground'>
                            charged
                        </span>
                    </div>
                </div>

                <dl className='grid flex-1 gap-3'>
                    {facts.map((fact) => (
                        <div key={fact.label} className='flex items-baseline justify-between gap-3 border-b border-black/5 pb-2.5 last:border-0 last:pb-0'>
                            <dt className='text-sm text-muted-foreground'>
                                {fact.label}
                            </dt>
                            <dd className='text-sm font-medium text-foreground'>
                                {fact.value}
                            </dd>
                        </div>
                    ))}
                </dl>
            </div>

            <div className='mt-7'>
                <div className='flex flex-wrap items-center justify-between gap-2'>
                    <p className='text-xs font-medium text-foreground'>
                        Ridgeway battery, today
                    </p>

                    <ul className='flex items-center gap-3 text-[11px] text-muted-foreground'>
                        <li className='flex items-center gap-1.5'>
                            <span aria-hidden='true' className='size-2.5 rounded-[3px] bg-primary' />
                            Charge
                        </li>
                        <li className='flex items-center gap-1.5'>
                            <span aria-hidden='true' className='size-2.5 rounded-[3px] bg-chart-2' />
                            Discharge
                        </li>
                        <li className='flex items-center gap-1.5'>
                            <span aria-hidden='true' className='size-2.5 rounded-[3px] bg-mist ring-1 ring-black/5' />
                            Idle
                        </li>
                    </ul>
                </div>

                <div
                    role='img'
                    aria-label='Battery plan: charge from 11:00 to 15:00, discharge from 17:00 to 21:00, idle otherwise'
                    className='mt-3 grid grid-cols-24 gap-[2px]'
                >
                    {plan.map((state, hour) => (
                        <span
                            key={hour}
                            className={cn(
                                'h-9 rounded-[4px]',
                                slotStyles[state],
                                hour === CURRENT_HOUR && 'ring-2 ring-sun ring-offset-1 ring-offset-white'
                            )}
                        />
                    ))}
                </div>

                <div aria-hidden='true' className='mt-2 flex justify-between text-[11px] tabular-nums text-muted-foreground'>
                    <span>00:00</span>
                    <span>06:00</span>
                    <span>12:00</span>
                    <span>18:00</span>
                    <span>24:00</span>
                </div>
            </div>
        </div>
    )
}

export default BalanceVisual
